import * as THREE from 'three';
import { assemblePivot } from './pivotSections';
import { PIVOT_CAD as CAD, PIVOT_DEFAULT_PERCENT } from '../configurator/pivotRules';
import { dividerArray } from '../configurator/dividerArray';

export function makePivotDoor(product, materials, options = {}) {
  const width = options.width ?? product.width, height = options.height ?? product.height;
  const percent = options.pivotPercent ?? PIVOT_DEFAULT_PERCENT, direction = options.direction || 'inward';
  const group = new THREE.Group(), frame = options.frame || options.frameMaterial || materials.makeFrame();
  const roles = { frame, thermal: materials.thermal, gasket: materials.gasket };
  assemblePivot(group, CAD.frameCode, ['left', 'right', 'head'], width, height, roles, true);
  assemblePivot(group, CAD.sillCode, ['sill'], width, height, roles);
  const leafWidth = width - CAD.leftInset - CAD.rightInset, leafHeight = height - CAD.topInset - CAD.bottomInset;
  const axisX = -width / 2 + CAD.leftInset + leafWidth * percent / 100;
  const pivot = new THREE.Group(), body = new THREE.Group(); pivot.name = 'pivot-door-axis'; body.name = 'pivot-door-leaf';
  // Supplied closing-jamb section reverses leaf depth relative to the frame.
  // Keep the actual 17.414mm rebate overlap: mirror the profile, never shrink it.
  assemblePivot(body, CAD.frameCode, ['left', 'right', 'head', 'sill'], leafWidth, leafHeight, roles, false, true);
  const bite = CAD.face - CAD.glazingBite, gw = leafWidth - 2 * bite, gh = leafHeight - 2 * bite;
  const glass = new THREE.Mesh(new THREE.BoxGeometry(gw, gh, CAD.glassDepth), materials.glass);
  glass.name = 'pivot-door-glass'; glass.position.y = leafHeight / 2; glass.receiveShadow = true; body.add(glass);
  if (options.dividers) dividerArray(body, 1, leafWidth, leafHeight, options.dividers, frame, { glassWidth: gw, glassHeight: gh, glassCenterY: leafHeight / 2 })([{ x: 0, z: 0 }]);
  addHandle(body, leafWidth, leafHeight, percent, materials.steel);
  body.position.x = leafWidth * (.5 - percent / 100); pivot.add(body);
  pivot.position.set(axisX, CAD.bottomInset, 0); group.add(pivot);
  // The fixed bearings share the same axis as the entire rotating leaf.
  for (const [y, length] of [[.0245, .013], [height - CAD.face - .008, .016]]) {
    const bearing = new THREE.Mesh(new THREE.CylinderGeometry(.012, .012, length, 16), materials.steel);
    bearing.position.set(axisX, y, 0); bearing.name = 'pivot-bearing'; group.add(bearing);
  }
  const animate = fraction => {
    const degrees = THREE.MathUtils.clamp(fraction, 0, 1) * 90;
    // Main (longer) edge moves inside/outside; the short tail counter-swings.
    const sign = (percent <= 50 ? 1 : -1) * (direction === 'inward' ? 1 : -1);
    pivot.rotation.y = THREE.MathUtils.degToRad(degrees) * sign;
    group.userData.pivotAngle = degrees; group.userData.pivotSignedAngle = degrees * sign;
  };
  Object.assign(group.userData, { pivotDoor: true, pivotOffsetPercent: percent, pivotAxisX: axisX, pivotDirection: direction, leafWidth, leafDepthMm: CAD.depth * 1000, leafPivot: pivot, leafBody: body });
  group.traverse(o => { if (o.isMesh) o.userData.productId = product.id; });
  animate(options.fraction || 0);
  return { group, frame, animate, progress: 0, raised: 0, depth: CAD.depth, panelWidth: leafWidth, panelHeight: leafHeight, count: 1 };
}

function addHandle(body, width, height, percent, steel) {
  const side = percent > 50 ? -1 : 1, x = side * (width / 2 - .058), y = Math.min(1.05, height * .48);
  for (const z of [-1, 1]) {
    const rose = new THREE.Mesh(new THREE.CylinderGeometry(.017, .017, .01, 16), steel); rose.rotation.x = Math.PI / 2; rose.position.set(x, y, z * .05); body.add(rose);
    const grip = new THREE.Mesh(new THREE.BoxGeometry(.105, .014, .018), steel); grip.position.set(x - side * .046, y, z * .069); grip.name = 'pivot-door-handle'; body.add(grip);
  }
}

export function makePivotSpecimen(product, type, frame, materials) {
  const group = new THREE.Group(), roles = { frame, thermal: materials.thermal, gasket: materials.gasket }, width = .38;
  assemblePivot(group, type === 'sill' ? CAD.sillCode : CAD.frameCode, ['sill'], width, .2, roles, type !== 'sill');
  const leaf = new THREE.Group(); leaf.position.y = type === 'sill' ? CAD.bottomInset : CAD.topInset;
  assemblePivot(leaf, CAD.frameCode, ['sill'], width - .02, .2, roles, false, true); group.add(leaf);
  Object.assign(group.userData, { profileDepth: CAD.depth, profileCode: type === 'sill' ? CAD.sillCode : CAD.frameCode });
  group.traverse(o => { if (o.isMesh) o.userData.productId = product.id; }); return group;
}